import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore
} from '@mui/icons-material';

const DataAndAI = () => {
  const services = [
    {
      title: 'AI and Generative AI',
      description: 'Innovate and drive value for your enterprise with cutting-edge AI solutions',
      features: ['Generative AI Implementation', 'Machine Learning Models', 'AI Strategy Development', 'Responsible AI Practices'],
      benefits: ['Enhanced Innovation', 'Competitive Advantage', 'Automated Processes', 'Intelligent Insights']
    },
    {
      title: 'Data Modernization',
      description: 'Modernize your data estate for transformative business value',
      features: ['Data Architecture Design', 'Data Lake Implementation', 'ETL/ELT Processes', 'Data Quality Management'],
      benefits: ['Improved Data Quality', 'Faster Analytics', 'Reduced Costs', 'Better Decision Making']
    },
    {
      title: 'Data Analytics and Insights',
      description: 'Transform raw data into actionable business insights',
      features: ['Business Intelligence', 'Predictive Analytics', 'Real-time Dashboards', 'Data Visualization'],
      benefits: ['Data-Driven Decisions', 'Performance Optimization', 'Risk Mitigation', 'Revenue Growth']
    },
    {
      title: 'AI-Powered Automation',
      description: 'Automate business processes with intelligent AI solutions',
      features: ['Process Automation', 'Intelligent Document Processing', 'Chatbots and Virtual Assistants', 'Workflow Optimization'],
      benefits: ['Increased Efficiency', 'Reduced Manual Work', 'Improved Accuracy', 'Cost Savings']
    }
  ];

  const customerStories = [
    {
      title: 'Large Global Convenience Store Chain',
      description: 'Enabling resilient digital banking services for a diversified economy',
      outcome: 'Kyndryl developed an ESL-controlled dynamic pricing solution that competitively prices expiring stocks. The successful pilot led to further deployment across 12,000 stores.',
      industry: 'Retail',
      services: ['AI-Powered Pricing', 'Data Analytics']
    },
    {
      title: 'United Arab Bank',
      description: 'Data transformation for excellent customer experience',
      outcome: 'Kyndryl helps United Arab Bank to transform its data assets, complementing the bank\'s efforts to enhance customer experiences, boost employee productivity, and ensure effective data management.',
      industry: 'Banking',
      services: ['Data Transformation', 'Customer Analytics']
    }
  ];

  const faqs = [
    {
      question: 'What are data and artificial intelligence services?',
      answer: 'Data and artificial intelligence (AI) services encompass a wide range of solutions designed to help organizations leverage data and AI technologies to generate business value. The services typically include data management, analytics, machine learning, and AI-driven automation to drive innovation, improve efficiency, and achieve strategic objectives.'
    },
    {
      question: 'How can AI-powered data services improve customer experiences?',
      answer: 'AI-powered data services enhance customer experiences by personalizing interactions and increasing efficiency. They offer tailored marketing, modernized customer service, and predictive analytics, improving engagement and loyalty while reducing frustration and boosting satisfaction.'
    },
    {
      question: 'How do you support businesses in adopting generative AI?',
      answer: 'Our AI-readiness program prepares businesses for generative AI integration, emphasizing innovation and data management. We provide advanced AI solutions and comprehensive support to drive AI innovation in enterprises, helping organizations leverage AI for business growth and transformation.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 3
            }}
          >
            Data and Artificial Intelligence Services
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Unlocking value from data and AI faster to help you scale and transform your digital business
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* What We Do Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Improve the use of data insights in decision-making
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 8,
              lineHeight: 1.6
            }}
          >
            Gather business insights at scale with a robust data foundation, modernization, and platform management. Our global expertise—combined with the latest innovations from technology partners and hyperscalers—prepares your business for artificial intelligence.
          </Typography>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Our Data and AI Services
          </Typography>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Key Features:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.features.map((feature, idx) => (
                          <Chip
                            key={idx}
                            label={feature}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Benefits:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.benefits.map((benefit, idx) => (
                          <Chip
                            key={idx}
                            label={benefit}
                            size="small"
                            sx={{
                              bgcolor: '#f3e5f5',
                              color: '#7b1fa2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Customer Stories Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Customer Success Stories
          </Typography>

          <Grid container spacing={4}>
            {customerStories.map((story, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {story.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1976d2',
                        mb: 2
                      }}
                    >
                      {story.description}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {story.outcome}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={story.industry}
                        sx={{
                          bgcolor: '#e8f5e8',
                          color: '#2e7d32',
                          fontWeight: 'bold'
                        }}
                      />
                      <Stack direction="row" spacing={1}>
                        {story.services.map((service, idx) => (
                          <Chip
                            key={idx}
                            label={service}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  mb: 2,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'white', borderRadius: '0 0 8px 8px' }}>
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Start Your Data and AI Journey
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Talk to an expert about the next step on your data modernization journey
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Request a Consultation
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default DataAndAI;
